import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Badge, Button } from "@vimos/ui";
import {
  fetchDirectoryBillingAccounts,
  fetchDirectoryBillingCharges,
  generateRecurringBillingCharges,
  markDirectoryBillingChargePaid,
  markDirectoryBillingChargePending,
  registerDirectoryBillingReminder,
  updateDirectoryBillingStatus,
  type DirectoryBillingCharge,
} from "./directoryApi";

const money = new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" });
const date = new Intl.DateTimeFormat("es-PE", { dateStyle: "medium", timeZone: "UTC" });
const PAGE_SIZE = 30;
const ACCOUNT_PAGE_SIZE = 20;
type ChargeBucket = "all" | "pending" | "overdue" | "upcoming" | "paid";

function localDateString(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

function formatDate(value: string) {
  return date.format(new Date(`${value}T00:00:00Z`));
}

function reminderMessage(charge: DirectoryBillingCharge) {
  return `Hola, ${charge.store_name}. Te escribimos para recordarte el pago de ${charge.description} por ${money.format(Number(charge.amount))}, con vencimiento ${formatDate(charge.due_date)}. Cuando lo realices, puedes enviarnos el comprobante por este medio. ¡Gracias!`;
}

function whatsappUrl(charge: DirectoryBillingCharge) {
  const phone = (charge.contact_phone ?? "").replace(/\D/g, "");
  if (!phone) return null;
  const internationalPhone = phone.startsWith("51") ? phone : `51${phone}`;
  return `https://wa.me/${internationalPhone}?text=${encodeURIComponent(reminderMessage(charge))}`;
}

const bucketOptions: Array<{ id: ChargeBucket; label: string }> = [
  { id: "all", label: "Todos" },
  { id: "overdue", label: "Vencidos" },
  { id: "upcoming", label: "Vencen en 7 días" },
  { id: "pending", label: "Pendientes" },
  { id: "paid", label: "Pagados" },
];

export function BillingPanel() {
  const queryClient = useQueryClient();
  const [bucket, setBucket] = useState<ChargeBucket>("overdue");
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [accountSearch, setAccountSearch] = useState("");
  const [accountQuery, setAccountQuery] = useState("");
  const [accountPage, setAccountPage] = useState(1);
  const [accountStatus, setAccountStatus] = useState("all");
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const today = localDateString();

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchDraft.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [searchDraft]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setAccountQuery(accountSearch.trim());
      setAccountPage(1);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [accountSearch]);

  const { data: queue, isLoading, error } = useQuery({
    queryKey: ["directory_billing_charges", bucket, search, page, today],
    queryFn: () => fetchDirectoryBillingCharges({ bucket, search, page, today }),
  });
  const {
    data: accountsPage,
    isLoading: isLoadingAccounts,
    error: accountsError,
  } = useQuery({
    queryKey: ["directory_billing_accounts", accountQuery, accountStatus, accountPage],
    queryFn: () => fetchDirectoryBillingAccounts({ search: accountQuery, status: accountStatus, page: accountPage }),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["directory_billing_charges"] });
    queryClient.invalidateQueries({ queryKey: ["directory_billing_accounts"] });
  };
  const generate = useMutation({
    mutationFn: generateRecurringBillingCharges,
    onSuccess: (count) => {
      refresh();
      setActionMessage(count === 0 ? "No había nuevos cobros recurrentes por generar." : `Se generaron ${count} cobros recurrentes.`);
    },
  });
  const setPaid = useMutation({
    mutationFn: (charge: DirectoryBillingCharge) =>
      charge.status === "paid"
        ? markDirectoryBillingChargePending(charge.id)
        : markDirectoryBillingChargePaid(charge.id),
    onSuccess: (_result, charge) => {
      refresh();
      setActionMessage(charge.status === "paid" ? "El cobro volvió a quedar pendiente." : "Pago registrado.");
    },
  });
  const recordReminder = useMutation({
    mutationFn: registerDirectoryBillingReminder,
    onSuccess: () => {
      refresh();
      setActionMessage("Envío del recordatorio registrado.");
    },
  });
  const updateAccount = useMutation({
    mutationFn: ({ storeId, status }: { storeId: string; status: "active" | "paused" | "cancelled" }) =>
      updateDirectoryBillingStatus(storeId, status),
    onSuccess: () => {
      refresh();
      setActionMessage("Estado del contrato actualizado.");
    },
  });

  const accountPageCount = Math.max(1, Math.ceil((accountsPage?.total ?? 0) / ACCOUNT_PAGE_SIZE));
  const visibleAccounts = accountsPage?.items ?? [];
  const pageCount = Math.max(1, Math.ceil((queue?.total_count ?? 0) / PAGE_SIZE));
  const metrics = queue?.metrics;

  function changeBucket(next: ChargeBucket) {
    setBucket(next);
    setPage(1);
  }

  function changeAccountFilter(next: () => void) {
    next();
    setAccountPage(1);
  }

  return (
    <section className="mt-4 space-y-5">
      <div className="border-sand/50 bg-paper rounded-lg border p-4 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="font-display text-ink text-lg font-bold">Cartera y seguimiento</h2>
            <p className="text-mist mt-1 max-w-2xl text-sm">
              Atiende primero los vencidos, prepara avisos por WhatsApp y deja registro cuando el mensaje se envíe.
            </p>
          </div>
          <Button onClick={() => generate.mutate()} isLoading={generate.isPending}>
            Generar renovaciones
          </Button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 xl:grid-cols-4">
          <Summary label="Saldo pendiente" value={money.format(Number(metrics?.pending_total ?? 0))} detail={`${metrics?.pending_count ?? 0} cobros`} />
          <Summary label="Vencido" value={money.format(Number(metrics?.overdue_total ?? 0))} detail={`${metrics?.overdue_count ?? 0} requieren seguimiento`} tone="danger" />
          <Summary label="Próximos 7 días" value={money.format(Number(metrics?.upcoming_total ?? 0))} detail={`${metrics?.upcoming_count ?? 0} por vencer`} />
          <Summary label="Cobrado histórico" value={money.format(Number(metrics?.paid_total ?? 0))} detail={`${metrics?.paid_count ?? 0} pagos registrados`} />
        </div>

        {actionMessage ? (
          <p className="mt-4 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
            {actionMessage}
          </p>
        ) : null}
        {generate.error ? <ErrorMessage error={generate.error} fallback="No se pudieron generar los cobros recurrentes." /> : null}
        {setPaid.error ? <ErrorMessage error={setPaid.error} fallback="No se pudo actualizar el pago." /> : null}
        {recordReminder.error ? <ErrorMessage error={recordReminder.error} fallback="No se pudo registrar el recordatorio." /> : null}

        <div className="mt-6 flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar cobros">
            {bucketOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                aria-pressed={bucket === option.id}
                onClick={() => changeBucket(option.id)}
                className={`rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                  bucket === option.id
                    ? "border-blue-200 bg-blue-50 text-blue-800"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <label className="block w-full xl:max-w-sm">
            <span className="text-mist mb-1 block text-xs font-medium">Buscar negocio o concepto</span>
            <input
              type="search"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder="Nombre del negocio…"
              className="border-sand/60 bg-paper text-ink placeholder:text-mist h-10 w-full rounded-md border px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            />
          </label>
        </div>

        {isLoading ? <p className="text-mist mt-5 text-sm">Cargando cartera…</p> : null}
        {error ? <p className="mt-5 text-sm text-red-600">No se pudieron cargar los cobros.</p> : null}
        {!isLoading && !error ? (
          <>
            <div className="border-sand/40 mt-4 overflow-x-auto rounded-lg border">
              <table className="w-full min-w-[920px] border-collapse text-left text-sm">
                <thead className="bg-sand/20 text-mist text-xs uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-3">Negocio y contacto</th>
                    <th className="px-3 py-3">Concepto</th>
                    <th className="px-3 py-3">Monto</th>
                    <th className="px-3 py-3">Vencimiento</th>
                    <th className="px-3 py-3">Estado</th>
                    <th className="px-3 py-3">Último aviso</th>
                    <th className="px-3 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-sand/30 divide-y">
                  {(queue?.items ?? []).map((charge) => {
                    const overdue = charge.status === "pending" && charge.due_date < today;
                    const messageUrl = whatsappUrl(charge);
                    return (
                      <tr key={charge.id} className={overdue ? "bg-red-50/40" : ""}>
                        <td className="text-ink px-3 py-3 font-medium">
                          {charge.store_id ? <Link to={`/directorio/${charge.store_id}`} className="block hover:text-blue-700 hover:underline">{charge.store_name}</Link> : <span className="block">{charge.store_name}</span>}
                          <span className="text-mist mt-0.5 block text-xs">
                            {charge.contact_phone ? `WhatsApp ${charge.contact_phone}` : "Sin teléfono registrado"}
                          </span>
                        </td>
                        <td className="text-mist px-3 py-3">{charge.description}</td>
                        <td className="text-ink px-3 py-3 tabular-nums">{money.format(Number(charge.amount))}</td>
                        <td className="text-mist px-3 py-3">{formatDate(charge.due_date)}</td>
                        <td className="px-3 py-3">
                          <Badge
                            tone={charge.status === "paid" ? "signal" : charge.status === "pending" ? "market" : "neutral"}
                            className={overdue ? "bg-red-100 text-red-700" : ""}
                          >
                            {charge.status === "paid" ? "Pagado" : overdue ? "Vencido" : charge.status === "pending" ? "Pendiente" : "Anulado"}
                          </Badge>
                        </td>
                        <td className="text-mist px-3 py-3 text-xs">
                          {charge.last_reminder_at
                            ? `${date.format(new Date(charge.last_reminder_at))} · ${charge.reminder_count} avisos`
                            : "Sin aviso registrado"}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            {charge.status === "pending" ? (
                              <>
                                {messageUrl ? (
                                  <a
                                    href={messageUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex h-9 items-center rounded-md border border-emerald-200 bg-emerald-50 px-2.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                                  >
                                    Preparar WhatsApp
                                  </a>
                                ) : null}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={recordReminder.isPending}
                                  onClick={() => recordReminder.mutate(charge.id)}
                                >
                                  Registrar enviado
                                </Button>
                              </>
                            ) : null}
                            {charge.status !== "void" ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={setPaid.isPending}
                                onClick={() => setPaid.mutate(charge)}
                              >
                                {charge.status === "paid" ? "Reabrir" : "Marcar pagado"}
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {queue?.items.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-mist px-3 py-8 text-center">
                        {bucket === "overdue" ? "No hay cobros vencidos. La cola está al día." : "No hay cobros que coincidan con este filtro."}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {(queue?.total_count ?? 0) > 0 ? (
              <div className="mt-4 flex items-center justify-between gap-3">
                <p className="text-mist text-xs">
                  {queue?.total_count} cobros · página {page} de {pageCount} · mostrando hasta {PAGE_SIZE} por página
                </p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
                    Anterior
                  </Button>
                  <Button size="sm" variant="outline" disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)}>
                    Siguiente
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="border-sand/50 bg-paper rounded-lg border p-4 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="font-display text-ink text-lg font-bold">Contratos recurrentes</h2>
            <p className="text-mist mt-1 text-sm">Busca, revisa la periodicidad y pausa cuentas sin recorrer toda la cartera.</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="search"
              aria-label="Buscar cuenta de cobro recurrente"
              value={accountSearch}
              onChange={(event) => changeAccountFilter(() => setAccountSearch(event.target.value))}
              placeholder="Buscar negocio…"
              className="border-sand/60 bg-paper text-ink h-9 rounded-md border px-3 text-sm"
            />
            <select
              aria-label="Filtrar estado del contrato"
              value={accountStatus}
              onChange={(event) => changeAccountFilter(() => setAccountStatus(event.target.value))}
              className="border-sand/60 bg-paper text-ink h-9 rounded-md border px-2 text-sm"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Activas</option>
              <option value="paused">Pausadas</option>
              <option value="cancelled">Canceladas</option>
            </select>
          </div>
        </div>
        {accountsError ? <p className="mt-3 text-sm text-red-600">No se pudieron cargar los contratos.</p> : null}
        {isLoadingAccounts ? <p className="text-mist mt-3 text-sm">Cargando contratos…</p> : null}
        {!isLoadingAccounts && !accountsError ? (
          <>
            <div className="border-sand/40 mt-4 overflow-x-auto rounded-lg border">
              <table className="w-full min-w-[680px] border-collapse text-left text-sm">
                <thead className="bg-sand/20 text-mist text-xs uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-3">Negocio</th>
                    <th className="px-3 py-3">Plan</th>
                    <th className="px-3 py-3">Monto</th>
                    <th className="px-3 py-3">Periodicidad</th>
                    <th className="px-3 py-3">Inicio</th>
                    <th className="px-3 py-3">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-sand/30 divide-y">
                  {visibleAccounts.map((account) => (
                    <tr key={account.id}>
                      <td className="text-ink px-3 py-3 font-medium">
                        <Link to={`/directorio/${account.id}`} className="hover:text-blue-700 hover:underline">{account.name}</Link>
                        {account.billing_is_comped ? <span className="text-emerald-700 mt-1 block text-xs font-normal">Cortesía · sin cobros</span> : null}
                      </td>
                      <td className="text-mist px-3 py-3">
                        {account.billing_plan_name ?? "Personalizado"}
                        {account.billing_price_note ? <span className="mt-1 block text-xs">{account.billing_price_note}</span> : null}
                      </td>
                      <td className="text-ink px-3 py-3 tabular-nums">{account.billing_is_comped ? "Gratis" : money.format(account.billing_amount)}</td>
                      <td className="text-mist px-3 py-3">{account.billing_is_comped ? "Cortesía" : account.billing_period === "yearly" ? "Anual" : account.billing_period === "one_time" ? "Único" : "Mensual"}</td>
                      <td className="text-mist px-3 py-3">
                        {account.billing_start_date ? formatDate(account.billing_start_date) : "—"}
                      </td>
                      <td className="px-3 py-3">
                        <select
                          aria-label={`Estado del contrato de ${account.name}`}
                          className="border-sand/60 bg-paper text-ink h-9 rounded-md border px-2 text-sm"
                          value={account.billing_status}
                          disabled={updateAccount.isPending}
                          onChange={(event) =>
                            updateAccount.mutate({
                              storeId: account.id,
                              status: event.target.value as "active" | "paused" | "cancelled",
                            })
                          }
                        >
                          <option value="active">Activa</option>
                          <option value="paused">Pausada</option>
                          <option value="cancelled">Cancelada</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                  {visibleAccounts.length === 0 ? (
                    <tr><td colSpan={6} className="text-mist px-3 py-6 text-center">No hay cuentas con esos filtros.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-mist text-xs">{accountsPage?.total ?? 0} cuentas encontradas · mostrando {ACCOUNT_PAGE_SIZE} por página</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={accountPage <= 1} onClick={() => setAccountPage((current) => current - 1)}>Anterior</Button>
                <span className="text-mist self-center text-xs">{accountPage} / {accountPageCount}</span>
                <Button size="sm" variant="outline" disabled={accountPage >= accountPageCount} onClick={() => setAccountPage((current) => current + 1)}>Siguiente</Button>
              </div>
            </div>
          </>
        ) : null}
        {updateAccount.error ? <ErrorMessage error={updateAccount.error} fallback="No se pudo actualizar el contrato." /> : null}
      </div>
    </section>
  );
}

function Summary({ label, value, detail, tone = "normal" }: { label: string; value: string; detail: string; tone?: "normal" | "danger" }) {
  return (
    <div className={`rounded-lg border px-4 py-3 ${tone === "danger" ? "border-red-100 bg-red-50/50" : "border-sand/40"}`}>
      <p className="text-mist text-xs font-medium uppercase tracking-wide">{label}</p>
      <p className={`font-display mt-1 text-xl font-bold ${tone === "danger" ? "text-red-700" : "text-ink"}`}>{value}</p>
      <p className="text-mist mt-1 text-xs">{detail}</p>
    </div>
  );
}

function ErrorMessage({ error, fallback }: { error: unknown; fallback: string }) {
  return <p className="mt-2 text-sm text-red-600">{error instanceof Error ? error.message : fallback}</p>;
}
